import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Facebook, Globe } from 'lucide-react';
import BrightforgeMark from '../BrightforgeMark';
import { PIXLIE_SUPPORT_EMAIL } from '../../constants/support';

const XSocialIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const BrandWordmark: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <Link to="/" className="flex items-center gap-2 group">
    <BrightforgeMark className={compact ? 'h-8 w-8' : 'h-10 w-10'} />
    <span
      className={`font-black tracking-tighter text-white group-hover:text-secondary transition-colors ${compact ? 'text-lg' : 'text-xl'}`}
    >
      PIXLIE
    </span>
  </Link>
);

const footerPageHref = (page: string): string => {
  switch (page) {
    case 'Privacy Policy':
      return '/privacy/';
    case 'Terms of Service':
      return '/terms/';
    case 'Licenses':
      return '/?page=licenses';
    default:
      return '/';
  }
};

export const PublicFooter: React.FC = () => {
  const onNavigate = (page: string) => {
    window.location.assign(footerPageHref(page));
  };

  return (
    <footer className="relative z-10 animate-in border-t border-white/5 bg-black px-6 py-12 text-center text-sm duration-500 md:text-left">
      <div className="mx-auto mb-12 grid max-w-7xl grid-cols-2 gap-8 md:grid-cols-5">
        <div>
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Brand</h4>
          <ul className="space-y-3 text-white/50">
            <li>
              <button type="button" onClick={() => onNavigate('About')} className="text-left transition-colors hover:text-white">
                About
              </button>
            </li>
            <li>
              <button type="button" onClick={() => onNavigate('Careers')} className="text-left transition-colors hover:text-white">
                Careers
              </button>
            </li>
            <li>
              <button type="button" onClick={() => onNavigate('Blog')} className="text-left transition-colors hover:text-white">
                Blog
              </button>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Company</h4>
          <ul className="space-y-3 text-left text-xs leading-5 text-white/50">
            <li>
              <a href="https://brightforge.ltd/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-secondary">
                Brightforge Software Inc.
              </a>
              <span className="block text-white/30">Delaware C-Corporation</span>
              <span className="block text-white/30">8 The Green, STE B #25043</span>
              <span className="block text-white/30">Dover, DE 19901, USA</span>
            </li>
            <li>
              <span className="block text-white/50">Brightforge Software Oy</span>
              <span className="block text-white/30">Kaivokselantie 5C, mailbox 66</span>
              <span className="block text-white/30">01610 Vantaa, Finland</span>
            </li>
            <li>
              <a href={`mailto:${PIXLIE_SUPPORT_EMAIL}?subject=Pixlie%20Support`} className="transition-colors hover:text-secondary">
                {PIXLIE_SUPPORT_EMAIL}
              </a>
              <a href="tel:+19094972155" className="block text-white/30 transition-colors hover:text-secondary">
                +1 (909) 497-2155
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Support</h4>
          <ul className="space-y-3 text-white/50">
            <li>
              <Link to="/support" className="transition-colors hover:text-secondary">
                Contact support
              </Link>
            </li>
            <li>
              <button type="button" onClick={() => onNavigate('Help Center')} className="text-left transition-colors hover:text-white">
                Help Center
              </button>
            </li>
            <li>
              <a href="/terms/" className="transition-colors hover:text-white">
                Terms of Service
              </a>
            </li>
            <li>
              <a href="/privacy/" className="transition-colors hover:text-white">
                Privacy Policy
              </a>
            </li>
            <li>
              <Link to="/?page=licenses" className="transition-colors hover:text-white">
                Licenses & Compliance
              </Link>
            </li>
          </ul>
        </div>
        <div className="col-span-2">
          <h4 className="mb-4 text-xs font-bold uppercase tracking-widest text-white">Connect</h4>
          <div className="flex flex-wrap justify-center gap-3 md:justify-end">
            <a href="https://www.instagram.com/brightforge_software_inc" aria-label="Instagram" className="rounded-lg border border-white/10 p-2 text-white/50 transition-colors hover:border-secondary hover:text-secondary">
              <Instagram className="h-5 w-5" />
            </a>
            <a href="https://x.com/brightforge_inc" aria-label="X" className="rounded-lg border border-white/10 p-2 text-white/50 transition-colors hover:border-secondary hover:text-secondary">
              <XSocialIcon className="h-[18px] w-[18px]" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=61586741459448" aria-label="Facebook" className="rounded-lg border border-white/10 p-2 text-white/50 transition-colors hover:border-secondary hover:text-secondary">
              <Facebook className="h-5 w-5" />
            </a>
            <a href="https://brightforge.ltd/" aria-label="Brightforge website" className="rounded-lg border border-white/10 p-2 text-white/50 transition-colors hover:border-secondary hover:text-secondary">
              <Globe className="h-5 w-5" />
            </a>
          </div>
          <p className="mt-4 text-xs text-white/30">Stay close to the crew.</p>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 border-t border-white/5 pt-8 text-xs opacity-40 md:flex-row">
        <div className="flex flex-col items-center gap-4 md:flex-row">
          <p>© {new Date().getFullYear()} Brightforge Software Inc. · Brightforge Software Oy</p>
          <span className="hidden text-white/20 md:inline">•</span>
          <p className="text-white/50">
            AI powered by commercially licensed models.{' '}
            <Link to="/?page=licenses" className="ml-1 underline transition-colors hover:text-white">
              Learn more
            </Link>
          </p>
        </div>
        <p>Built for teams that actually ship.</p>
      </div>
    </footer>
  );
};

type PublicPageChromeProps = {
  children: React.ReactNode;
};

export const PublicPageChrome: React.FC<PublicPageChromeProps> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex min-h-screen animate-in flex-col bg-black text-white fade-in slide-in-from-bottom-4 duration-300">
      <nav className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between border-b border-white/5 bg-black/80 px-6 py-4 backdrop-blur-md">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="group flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white/60 transition-colors hover:text-secondary"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to Home
        </button>
        <BrandWordmark compact />
      </nav>
      <main className="mb-20 mt-20 w-full flex-1 px-6">{children}</main>
      <PublicFooter />
    </div>
  );
};
